/* eslint-disable custom-rules/no-restricted-imports-clone */
import { useContext, useEffect, useState } from "react";
import {
  SelectInput,
  TextInput,
  Typography
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { PreferencesContext } from "../providers/PreferencesProvider";
import type { IncentivesRecord } from "@dealer-center/ddc-pages-incentives-manager-interface";
import {
  getIncentiveTypesDropdown,
  typeContentChange,
  updateContent
} from "../utils/utils";
import { cashValidation, percentValidation } from "../utils/validation";
import "./DetailsPanel.scss";
import type { EditIncentivesDisableFlags } from "@dealer-center/ddc-pages-incentives-manager-interface";

interface AprState {
  hasTerm: boolean;
  isBadCustom: boolean;
  termErrors: string[];
}

interface AprState {
  hasTerm: boolean;
  isBadCustom: boolean;
  termErrors: string[];
}

const numberString = (value?: number): string => {
  if (typeof value === "number") {
    if (value === -1) {
      return "";
    }
    return value.toString();
  } else {
    return "";
  }
};

const makeInput = (
  label: string,
  name: string,
  propName: string,
  incentive,
  setIncentive: (
    updater: (prevState: IncentivesRecord) => IncentivesRecord
  ) => void,
  givenClass = "",
  error,
  inputPrefix = "",
  inputSuffix = "",
  hasError = false,
  handleAprStateChange,
  disableFlags: EditIncentivesDisableFlags
) => (
  <TextInput
    className={disableFlags.readOnly ? "" : givenClass}
    getError={error}
    hasError={hasError}
    inputPrefix={inputPrefix}
    inputSuffix={inputSuffix}
    key={propName + incentive.type} // Unique key to force re-render on prop change
    label={Locale.getText(label) as string}
    name={name}
    readOnly={disableFlags.readOnly}
    type="number"
    value={numberString(incentive[propName])}
    onChange={event => {
      let value = event.target.value;
      if (isNaN(Number(value))) {
        value = incentive[propName].toString();
      } else if (value.trim().length === 0) {
        value = "-1";
      }

      setIncentive(prevState => {
        const updatedIncentive = { ...prevState, [propName]: Number(value) };
        if (["CASH_OR_APR", "APR", "CASH_AND_APR"].includes(prevState.type)) {
          handleAprStateChange(updatedIncentive);
        }
        return updatedIncentive;
      });
    }}
  />
);

export const DetailsPanel = ({
  changeError,
  languageCode,
  disableFlags,
  setEditError,
  incentive,
  setIncentive
}: {
  changeError: (id: string, status: boolean) => void;
  disableFlags: EditIncentivesDisableFlags;
  languageCode: string;
  incentive: IncentivesRecord;
  setEditError: React.Dispatch<React.SetStateAction<string>>;
  setIncentive: (
    updater: (prevState: IncentivesRecord) => IncentivesRecord
  ) => void;
}) => {
  const percentInput = (
    label: string,
    name: string,
    propName: string,
    givenClass = "",
    hasError = false
  ) => {
    const isRequired = givenClass === "required";
    return makeInput(
      label,
      name,
      propName,
      incentive,
      setIncentive,
      givenClass,
      percentValidation(
        propName,
        incentive[propName]?.toString() || "",
        isRequired,
        changeError
      ),
      "",
      "%",
      hasError,
      handleAprStateChange,
      disableFlags
    );
  };
  const integerInput = (
    label: string,
    name: string,
    propName: string,
    givenClass = "",
    hasError = false
  ) => {
    const isRequired = givenClass === "required";
    return makeInput(
      label,
      name,
      propName,
      incentive,
      setIncentive,
      givenClass,
      percentValidation(
        propName,
        incentive[propName]?.toString() || "",
        isRequired,
        changeError
      ),
      "",
      "",
      hasError,
      handleAprStateChange,
      disableFlags
    );
  };
  const cashInput = (
    label: string,
    name: string,
    propName: string,
    givenClass = ""
  ) => {
    const isRequired = givenClass === "required";
    return makeInput(
      label,
      name,
      propName,
      incentive,
      setIncentive,
      givenClass,
      cashValidation(
        propName,
        incentive[propName]?.toString() || "",
        isRequired,
        changeError
      ),
      "$",
      "",
      false,
      handleAprStateChange,
      disableFlags
    );
  };

  const { preferences } = useContext(PreferencesContext);
  const contentDefaults = preferences.contentDefaults || [];

  const showAprDetails = (aprState: AprState) => {
    const { hasTerm, isBadCustom, termErrors } = aprState;
    return (
      <>
        <Typography variant="h4">
          {Locale.getText("APR_DETAILS") as string}
        </Typography>
        <Typography className="text" variant="body-md">
          {Locale.getText("TERMS") as string}{" "}
          <span className="term-required">*</span>
        </Typography>
        <div className="term-num-panel">
          <div className="group">
            {percentInput("MO24", "24MO", "twentyFourMonthApr", "", !hasTerm)}
            {percentInput("MO36", "36MO", "thirtySixMonthApr", "", !hasTerm)}
          </div>
          <div className="group">
            {percentInput("MO39", "39MO", "thirtyNineMonthApr", "", !hasTerm)}
            {percentInput("MO42", "42MO", "fortyTwoMonthApr", "", !hasTerm)}
          </div>
          <div className="group">
            {percentInput("MO48", "48MO", "fortyEightMonthApr", "", !hasTerm)}
            {percentInput("MO60", "60MO", "sixtyMonthApr", "", !hasTerm)}
          </div>
          <div className="group">
            {percentInput("MO72", "72MO", "seventyTwoMonthApr", "", !hasTerm)}
          </div>
        </div>
        <div className="custom-panel">
          <div className="group">
            {integerInput(
              "CUSTOM_LENGTH",
              "custom-length",
              "customTermMonths",
              "",
              isBadCustom
            )}
            {percentInput(
              "CUSTOM_RATE",
              "custom-rate",
              "customApr",
              "",
              isBadCustom || !hasTerm
            )}
          </div>
          <div className="term-errors">
            {!disableFlags.readOnly && termErrors.length !== 0 && (
              <>
                <div>{Locale.getText("TERM_ERRORS") as string}</div>
                <ul className="term-errors">
                  {termErrors.map(error => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
        <div className="terms-payment-panel">
          <div className="group">
            {cashInput("DOWN_PAYMENT", "down-payment", "cashDue")}
            {cashInput("MSRP", "msrp", "msrp")}
          </div>
        </div>
      </>
    );
  };

  const showCashDetails = (type: string) => (
    <div className="bottom-panel">
      <Typography className="text" variant="h4">
        {Locale.getText("CASH_DETAILS") as string}
      </Typography>
      <div className="cash-details">
        {cashInput(
          "TOTAL_CASH_BACK",
          "total-cash-back",
          "cashOption",
          type.includes("CASH") ? "required" : ""
        )}
      </div>
    </div>
  );

  const showLeaseDetails = () => (
    <>
      <Typography variant="h4">
        {Locale.getText("LEASE_DETAILS") as string}
      </Typography>
      <div className="custom-panel">
        <div className="group">
          {cashInput(
            "MONTHLY_PAYMENT",
            "monthly-payment",
            "monthlyPayment",
            "required"
          )}
          {integerInput(
            "LEASE_TERM",
            "lease-term",
            "customTermMonths",
            "required"
          )}
        </div>
      </div>
      <div className="custom-panel">
        <div className="group">
          {cashInput(
            "DUE_AT_LEASE_SIGNING",
            "due-to-lease-signing",
            "cashDue",
            "required"
          )}
          {cashInput("RESIDUAL_VALUE", "residual-value", "purchaseOptionPrice")}
        </div>
      </div>
      <div className="custom-panel">
        <div className="group">
          {cashInput(
            "MILEAGE_ALLOWANCE",
            "mileage-allowance",
            "mileageAllowance"
          )}
          {cashInput("OVERAGE_CHARGE", "overage-charge", "mileOverageCharge")}
        </div>
      </div>
      <div className="group">{cashInput("MSRP", "msrp", "msrp")}</div>
    </>
  );

  const showUndefinedTermArpDetails = () => (
    <>
      <Typography variant="h4">
        {Locale.getText("UNDEFINED_TERM_APR_DETAILS") as string}
      </Typography>
      <div className="custom-panel">
        <div className="group">
          {percentInput("ARP", "arp", "customApr", "required")}
          {cashInput("DOWN_PAYMENT", "down-payment", "cashDue")}
        </div>
        <div className="group">{cashInput("MSRP", "msrp", "msrp")}</div>
      </div>
    </>
  );

  // Reset all errors when incentive type changes as each types requires different fields
  // Leave validation to each type's required fields which occurs when they show up in the form
  //
  const resetChangeErrors = () => {
    changeError("twentyFourMonthApr", false);
    changeError("thirtySixMonthApr", false);
    changeError("thirtyNineMonthApr", false);
    changeError("fortyTwoMonthApr", false);
    changeError("fortyEightMonthApr", false);
    changeError("sixtyMonthApr", false);
    changeError("seventyTwoMonthApr", false);
    changeError("customTermMonths", false);
    changeError("customApr", false);
    changeError("cashOption", false);
    changeError("monthlyPayment", false);
    changeError("customTermMonths", false);
    changeError("cashDue", false);
    changeError("purchaseOptionPrice", false);
    changeError("mileageAllowance", false);
    changeError("mileOverageCharge", false);
    changeError("msrp", false);

    changeError("ONE_TERM_MUST_EXIST", false);
    changeError("NEED_BOTH_CUSTOM_FIELDS", false);
  };

  // Clear field values when incentive type changes to avoid carrying over irrelevant data
  const clearTypeSpecificFields = (newType: string) => {
    const clearedIncentive = {
      ...incentive,
      type: newType,
      typeString: newType
    };

    // Clear APR-related fields
    clearedIncentive.twentyFourMonthApr = -1;
    clearedIncentive.thirtySixMonthApr = -1;
    clearedIncentive.thirtyNineMonthApr = -1;
    clearedIncentive.fortyTwoMonthApr = -1;
    clearedIncentive.fortyEightMonthApr = -1;
    clearedIncentive.sixtyMonthApr = -1;
    clearedIncentive.seventyTwoMonthApr = -1;
    clearedIncentive.customApr = -1;

    // Clear lease-specific fields
    clearedIncentive.monthlyPayment = -1;
    clearedIncentive.purchaseOptionPrice = -1;
    clearedIncentive.mileageAllowance = -1;
    clearedIncentive.mileOverageCharge = -1;

    // Clear cash and common fields
    clearedIncentive.cashOption = -1;
    clearedIncentive.cashDue = -1;
    clearedIncentive.msrp = -1;
    clearedIncentive.customTermMonths = -1;

    return clearedIncentive as IncentivesRecord;
  };

  const updateType = event => {
    setIncentive(prevState => {
      const newType = event.target.value || "";
      const changeResult = typeContentChange(
        incentive,
        incentive.type,
        newType,
        contentDefaults,
        languageCode
      );
      let newIncentive = clearTypeSpecificFields(newType);

      if (changeResult.disclaimerChanged || changeResult.descriptionChanged) {
        if (changeResult.disclaimerChanged) {
          newIncentive = updateContent(
            newIncentive,
            "disclaimer",
            changeResult.newDisclaimer,
            languageCode
          );
        }

        if (changeResult.descriptionChanged) {
          newIncentive = updateContent(
            newIncentive,
            "content",
            changeResult.newDescription,
            languageCode
          );
        }
        setEditError(Locale.getText("TYPE_CONTENT_CHANGED") as string);
      }
      return newIncentive;
    });
    resetChangeErrors();
  };

  const handleAprStateChange = (incent = incentive) => {
    const termErrors: string[] = [];
    const hasTerm = [
      "twentyFourMonthApr",
      "thirtySixMonthApr",
      "thirtyNineMonthApr",
      "fortyTwoMonthApr",
      "fortyEightMonthApr",
      "sixtyMonthApr",
      "seventyTwoMonthApr",
      "customApr"
    ].some(item => incent[item] !== -1);
    const isBadCustom =
      (incent.customTermMonths === -1 || incent.customApr === -1) &&
      incent.customTermMonths !== incent.customApr;
    if (!hasTerm) {
      termErrors.push(Locale.getText("ONE_TERM_MUST_EXIST") as string);
      changeError("ONE_TERM_MUST_EXIST", true);
    } else {
      changeError("ONE_TERM_MUST_EXIST", false);
    }
    if (isBadCustom) {
      termErrors.push(Locale.getText("NEED_BOTH_CUSTOM_FIELDS") as string);
      changeError("NEED_BOTH_CUSTOM_FIELDS", true);
    } else {
      changeError("NEED_BOTH_CUSTOM_FIELDS", false);
    }
    const isOverlappingCustom = [24, 36, 39, 42, 48, 60, 72].includes(
      incent.customTermMonths || -1
    );
    if (isOverlappingCustom) {
      termErrors.push(Locale.getText("OVELAPPING_CUSTOM_TERM") as string);
      changeError("OVELAPPING_CUSTOM_TERM", true);
    } else {
      changeError("OVELAPPING_CUSTOM_TERM", false);
    }
    setAprState({ hasTerm, isBadCustom, termErrors });
  };

  useEffect(() => {
    if (["CASH_OR_APR", "APR", "CASH_AND_APR"].includes(incentive.type || "")) {
      handleAprStateChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incentive.type]);

  const [aprState, setAprState] = useState<AprState>({
    hasTerm: false,
    isBadCustom: false,
    termErrors: []
  });

  return (
    <div className="details-panel">
      <SelectInput
        disabled={disableFlags.disableTypeEditing || disableFlags.readOnly}
        displayDeselectOption={false}
        id="incentive-type-input"
        label={String(Locale.getText("INCENTIVE_TYPE"))}
        name="incentive-type"
        options={getIncentiveTypesDropdown()}
        value={incentive.type || "CASH"}
        onChange={updateType}
      />
      {["CASH_OR_APR", "APR", "CASH_AND_APR"].includes(incentive.type || "") &&
        showAprDetails(aprState)}
      {incentive.type === "LEASE" && showLeaseDetails()}
      {incentive.type === "APR_UNDEF" && showUndefinedTermArpDetails()}
      {incentive.type !== "PROMOTION" && showCashDetails(incentive.type || "")}
    </div>
  );
};
